import {
  Chip,
  IconButton,
  InputAdornment,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { DeleteOutline as DeleteIcon } from '@material-ui/icons';
import { FieldArrayWithId } from 'react-hook-form';

import { QuoteItemTypeChip } from '../Chips/QuoteItemTypeChip.component';
import { QuoteItem } from '../../types/quote.type';
import { CreateQuoteFormValues } from './CreateQuoteStepper.component';
import { computeTotalsLocal } from '../../utils/compute-totals-local.util';

type Props = {
  items: QuoteItem[];
  fields: FieldArrayWithId<CreateQuoteFormValues, 'items', 'fieldId'>[];
  onRemove: (index: number) => void;
  onQuantityChange: (index: number, value: number) => void;
  onPriceChange: (index: number, value: number) => void;
};

const EUR = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  header: {
    fontWeight: 600,
    fontSize: 14,
  },
  totalsRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    borderTop: '1px solid #e0e0e0',
  },
  totalChip: {
    borderRadius: 4,
    color: '#fff',
    fontWeight: 600,
  },
  totalChipHt: { backgroundColor: '#9e9e9e' },
  totalChipTva: { backgroundColor: '#ef6c00' },
  totalChipTtc: { backgroundColor: '#2e7d32' },
  quantityInput: {
    width: 80,
  },
  priceInput: {
    width: 110,
  },
  emptyRow: {
    textAlign: 'center' as const,
  },
});

export function QuoteItemsTable({ items, fields, onRemove, onQuantityChange, onPriceChange }: Props) {
  const classes = useStyles();

  const sanitizedItems = items.map((item) => ({
    price: Number(item.price) || 0,
    quantity: Number(item.quantity) || 0,
    tvaRate: item.tvaRate,
  }));

  const totals = computeTotalsLocal({ items: sanitizedItems });
  const subtotalHt = totals.totalHt;
  const subtotalTva = Object.values(totals.taxes).reduce((sum, tax) => sum + tax, 0);
  const subtotalTtc = totals.totalTtc;

  const handleQuantityChange = (index: number, value: string) => {
    const parsed = Number(value);
    onQuantityChange(index, Number.isFinite(parsed) ? parsed : 0);
  };

  const handlePriceChange = (index: number, value: string) => {
    const parsed = Number(value);
    onPriceChange(index, Number.isFinite(parsed) ? parsed : 0);
  };

  return (
    <section className={classes.root}>
      <Typography className={classes.header}>Produits du devis ({fields.length})</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 120 }} />
              <TableCell style={{ fontWeight: 'bold' }}>Nom</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Référence</TableCell>
              <TableCell style={{ fontWeight: 'bold' }} align="right">
                Quantité
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }} align="right">
                Prix unitaire TTC
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }} align="right">
                Total TTC
              </TableCell>
              <TableCell style={{ width: 50 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((field, index) => {
              const item = items[index];
              if (!item) return null;
              const quantity = Number(item.quantity) || 0;
              const price = Number(item.price) || 0;
              const lineTotal = quantity * price;
              return (
                <TableRow key={field.fieldId}>
                  <TableCell>
                    <QuoteItemTypeChip type={item.type} />
                  </TableCell>
                  <TableCell>{item?.name}</TableCell>
                  <TableCell>{item?.ref}</TableCell>
                  <TableCell align="right">
                    <TextField
                      className={classes.quantityInput}
                      size="small"
                      variant="outlined"
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                      value={item?.quantity ?? 0}
                      onChange={(event) => handleQuantityChange(index, event.target.value)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      className={classes.priceInput}
                      size="small"
                      variant="outlined"
                      type="number"
                      inputProps={{ min: 0, step: 0.01 }}
                      value={item?.price ?? 0}
                      onChange={(event) => handlePriceChange(index, event.target.value)}
                      InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                    />
                  </TableCell>
                  <TableCell align="right">{lineTotal.toFixed(2)} €</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => onRemove(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {fields.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className={classes.emptyRow}>
                  Aucun produit ni panier ajouté
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <div className={classes.totalsRow}>
        <Chip
          size="small"
          label={`${EUR.format(subtotalHt)} HT`}
          className={`${classes.totalChip} ${classes.totalChipHt}`}
        />
        <Chip
          size="small"
          label={`${EUR.format(subtotalTva)} TVA`}
          className={`${classes.totalChip} ${classes.totalChipTva}`}
        />
        <Chip
          size="small"
          label={`${EUR.format(subtotalTtc)} TTC`}
          className={`${classes.totalChip} ${classes.totalChipTtc}`}
        />
      </div>
    </section>
  );
}
